// System
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

// General
import { generalReducer } from '@Modal/reducers/general/generalSlice';

// Authorization
import { authorizationReducer } from '@Modal/reducers/authorization/authorizationSlice';

// Roles
import { rolesReducer } from '@Modal/reducers/roles/rolesSlice';
import { rolesTableReducer } from '@Modal/reducers/roles/rolesTableSlice';
import { addUserReducer } from '@Modal/reducers/roles/addUserSlice';
import { rolesDrawerReducer } from '@Modal/reducers/roles/rolesDrawerSlice';
import { aviatorPermissionsReducer } from '@Modal/reducers/aviatorRoles/aviatorPermissionsSlice';

// Providers
import { providerTableReducer } from '@Modal/reducers/providers/table/providerTableSlice';
import { createProviderReducer } from '@Modal/reducers/providers/createProvider/CreateProviderSlice';
import { providerDrawerReducer } from '@Modal/reducers/providers/drawer/providerDrawerSlice';
import { createProviderConfigReducer } from '@Modal/reducers/providers/createProviderConfig/createProviderConfigSlice';
import { updateProviderConfigReducer } from '@Modal/reducers/providers/updateProviderConfigSlice/updateProviderConfigSlice';

// Rounds
import { roundTableReducer } from '@Modal/reducers/rounds/table/roundsTableSlice';
import { roundDrawerReducer } from '@Modal/reducers/rounds/drawer/roundDrawerSlice';
import { createRoundConfigReducer } from '@Modal/reducers/rounds/createRoundConfig/CreateRoundConfigSlice';

// Transaction
import { userTableReducer } from '@Modal/reducers/users/table/userTableSlice';
import { createUserReducer } from '@Modal/reducers/users/createUser/createUserSlice';
import { transactionTableReducer } from '@Modal/reducers/transaction/table/transactionTableSlice';
import { avTransactionDrawerReducer } from '@Modal/reducers/transaction/drawer/avTransactionDrawerSlice';
import { providerTransactionTableReducer } from '@Modal/reducers/providerTransactions/providerTransactionTableSlice';

// Campaign
import { campaignTableReducer } from '@Modal/reducers/campaigns/table/campaignTableSlice';
import { freebetTableReducer } from '@Modal/reducers/freebets/freebetTableSlice';
import { freebetUsageTableReducer } from '@Modal/reducers/freebetsUsage/freebetUsageTableSlice';

// AviatorRoles
import { roleTableReducer } from '@Modal/reducers/aviatorRoles/roleTableSlice';
import { aviatorRoleDrawerReducer } from '@Modal/reducers/aviatorRoles/aviatorRoleDrawerSlice';
import { createAviatorRoleReducer } from '@Modal/reducers/aviatorRoles/createAviatorRoleSlice';

// Analytics
import { analyticTableReducer } from '@Modal/reducers/analytics/table/analyticsTableSlice';

// Failed transactions
import { providerIgnoredRequestsTableReducer } from '@Modal/reducers/providerIgnoredRequests/providerIgnoredRequestsTableSlice';
import { failedSendCreditTasksTableReducer } from '@Modal/reducers/failedSendCreditTasks/failedSendCreidtTasksTableSlice';

// localization
import { localizationReducer } from '@Modal/reducers/localizations/localizationSlice';
import { failedSendCreditTransactionsTableReducer } from '@Modal/reducers/failedSendCreditTransactionsTableSlice/failedSendCreditTransactionsTableSlice';
import { scheduledSendCreditTasksTableReducer } from '@Modal/reducers/scheduledSendCreditTasks/scheduledSendCreditTasks';
import { userDrawerReducer } from '@Modal/reducers/users/drawer/userDrawerSlice';
import { createRaindropReducer } from '@Modal/reducers/raindrops/createRaindropSlice';
import { raindropTableReducer } from '@Modal/reducers/raindrops/raindropTableSlice';
import { createCampaignReducer } from '@Modal/reducers/campaigns/createCampaign/createCampaignSlice';
import { createFreebetsReducer } from '@Modal/reducers/freebets/createFreebets/createFreebetsSlice';
import { campaignDrawerReducer } from '@Modal/reducers/campaigns/drawer/campaignDrawerSlice';

// Persist configuration for general state
const generalPersistConfig = {
  key: 'general',
  storage,
  whitelist: ['rgs'], // Only persist the rgs field
};

const rootReducer = combineReducers({
  // Transactions
  // transactions: transactionsReducer,
  // transactionDrawer: transactionsDrawerReducer,
  // General
  general: persistReducer(generalPersistConfig, generalReducer),
  // Roles
  roles: rolesReducer,
  addUser: addUserReducer,
  rolesTable: rolesTableReducer,
  roleDrawer: rolesDrawerReducer,

  // Authorization
  authorization: authorizationReducer,

  // Providers
  providersTable: providerTableReducer,
  createProvider: createProviderReducer,
  providerDrawer: providerDrawerReducer,
  createProviderConfig: createProviderConfigReducer,
  updateProviderConfig: updateProviderConfigReducer,

  // Rounds
  roundsTable: roundTableReducer,
  roundDrawer: roundDrawerReducer,
  createRoundConfig: createRoundConfigReducer,

  // Transactions
  transactionTable: transactionTableReducer,
  providerTransactionTable: providerTransactionTableReducer,
  avTransactionDrawer: avTransactionDrawerReducer,
  providerIgnoredRequestsTable: providerIgnoredRequestsTableReducer,
  failedSendCreditTasksTable: failedSendCreditTasksTableReducer,
  failedSendCreditTransactionsTable: failedSendCreditTransactionsTableReducer,
  scheduledSendCreditTasksTable: scheduledSendCreditTasksTableReducer,

  // Users
  usersTable: userTableReducer,
  userDrawer: userDrawerReducer,
  createUser: createUserReducer,
  aviatorRolesTable: roleTableReducer,
  aviatorRoleDrawer: aviatorRoleDrawerReducer,
  createAviatorRole: createAviatorRoleReducer,
  aviatorPermissions: aviatorPermissionsReducer,

  // Campaigns
  campaignsTable: campaignTableReducer,
  freebetsTable: freebetTableReducer,
  freebetsUsageTable: freebetUsageTableReducer,
  createRaindrop: createRaindropReducer,
  raindropTable: raindropTableReducer,
  createCampaign: createCampaignReducer,
  createFreebets: createFreebetsReducer,
  campaignDrawer: campaignDrawerReducer,

  // Analytics
  analyticsTable: analyticTableReducer,

  // Localization
  localization: localizationReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
